import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Decryptedid } from "../../Util/BcruptEncyptid";
import LoaderComponent from "./LoaderComponent";
import {
  getAuthenticatedHomeRoute,
  getAuthToken,
  hasAuthenticatedUserData,
  isAllowedAuthenticatedRoute,
} from "../../Util/authRedirect";
import { organisationMemberDetailsAction } from "../../Redux/Action/organisationAction";
import { storeUser } from "../../Redux/Slice/authSlice";
import Api from "../../BaseUrlConfigration/ApiConfig";

const PrivateAuthRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const rehydrated = useSelector((state) => state?.authSlice?._persist?.rehydrated);
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const authToken = getAuthToken(userData);

  const userDataRef = useRef(userData);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const lastAuthCheckRef = useRef(0);

  // Background auth info checker for Trial Teachers on tab/route change
  useEffect(() => {
    if (rehydrated && authToken && userData?.role === "Teacher") {
      const now = Date.now();
      // Throttle: only fire at most once every 10 seconds to prevent spam
      if (now - lastAuthCheckRef.current > 10000) {
        lastAuthCheckRef.current = now;
        Api.get("school/auth/info").catch(() => {}); // interceptor in ApiConfig handles the rest
      }
    }
  }, [location.pathname, rehydrated, authToken, userData?.role]);

  // Refresh user permissions/data on page refresh for School Admins
  useEffect(() => {
    if (rehydrated && authToken) {
      const currentRole = userDataRef.current?.role;
      const currentAccountType = userDataRef.current?.accountType;
      
      const isSchoolAdmin = 
        currentRole === "School" || 
        currentAccountType === "School" || 
        currentRole === "SchoolAdmin" || 
        currentRole === "School Admin" ||
        currentAccountType === "School Admin" ||
        currentRole === "Super Admin";

      const isTeacher = 
        currentRole === "Teacher" || 
        currentAccountType === "Teacher";

      if ((isSchoolAdmin || isTeacher) && userDataRef.current?._id) {
        let payloadAccountType = "Teacher";
        if (currentAccountType === "School Admin" || currentRole === "School Admin" || currentRole === "SchoolAdmin") {
          payloadAccountType = "School Admin";
        } else if (isSchoolAdmin) {
          payloadAccountType = "School";
        }
        

        dispatch(
          organisationMemberDetailsAction({
            memberId: userDataRef.current._id,
            accountType: payloadAccountType, 
          })
        ).then((res) => {
          if (!res?.error) {
            const payloadData = res?.payload?.data || res?.payload || {};

            if (payloadData?.message === "No membership found for this user" || payloadData?.data === null) {
              dispatch(
                storeUser({
                  ...userDataRef.current,
                  groupRole: null,
                  organisationGroupRole: null,
                  organisationGroupId: null,
                  organisationGroupMemberships: [],
                })
              );
              return;
            }

            const memberDetails =
              payloadData?.member ||
              payloadData?.memberDetails ||
              payloadData?.details ||
              payloadData?.data ||
              payloadData ||
              {};
            if (memberDetails && Object.keys(memberDetails).length > 0) {
              
              // If memberDetails returns groupRole but no organisationGroupMemberships array,
              // we must update the existing array manually to avoid stale roles taking precedence.
              let updatedMemberships = memberDetails.organisationGroupMemberships || [];
              let updatedOrgGroupId = memberDetails.organisationGroupId || userDataRef.current.organisationGroupId || null;

              if (memberDetails.groupRole === null) {
                // If groupRole is explicitly null, remove them from the group
                if (userDataRef.current.organisationGroupMemberships) {
                  updatedMemberships = userDataRef.current.organisationGroupMemberships.filter((m) => {
                    const mId = m.organisationGroupId?._id || m.organisationGroupId;
                    const targetId = memberDetails.organisationGroupId || userDataRef.current.organisationGroupId;
                    return mId !== targetId;
                  });
                }
                
                if (updatedOrgGroupId === memberDetails.organisationGroupId || updatedOrgGroupId === userDataRef.current.organisationGroupId) {
                  updatedOrgGroupId = null; // Clear active group ID
                }
              } else if (!memberDetails.organisationGroupMemberships && userDataRef.current.organisationGroupMemberships) {
                updatedMemberships = userDataRef.current.organisationGroupMemberships.map((m) => {
                  const mId = m.organisationGroupId?._id || m.organisationGroupId;
                  const targetId = memberDetails.organisationGroupId || userDataRef.current.organisationGroupId;
                  if (mId === targetId && memberDetails.groupRole) {
                    return { ...m, groupRole: memberDetails.groupRole };
                  }
                  return m;
                });
              } else if (!memberDetails.organisationGroupMemberships && memberDetails.groupRole && memberDetails.organisationGroupId) {
                updatedMemberships = [{
                  organisationGroupId: memberDetails.organisationGroupId,
                  groupRole: memberDetails.groupRole
                }];
              }

              dispatch(
                storeUser({
                  ...userDataRef.current,
                  ...memberDetails,
                  organisationGroupId: updatedOrgGroupId,
                  organisationGroupMemberships: updatedMemberships,
                })
              );
            }
          }
        });
      }
    }
  }, [rehydrated, authToken, dispatch]);

  if (location.pathname.includes("/shared-collection/student/")) {
    const id = location.pathname.split("/shared-collection/student/")[1];
    let idval = id;
    try {
      idval = Decryptedid(atob(id));
      if (!idval || idval.trim() === "") idval = id;
    } catch (error) {
      idval = id;
    }
    window.location.replace(
      `https://peerscroller.onelink.me/gTJq/fydlv6xg?collectionId=${id}&af_force_deeplink=true`
    );
    return null;
  }

  if (location.pathname.includes("/video-shorts/")) {
    const decodedSearch = location.search.replace(/&amp;/g, "&");
    const urlParams = new URLSearchParams(decodedSearch);
    const role = urlParams.get("role");
    const isShareable = urlParams.get("isShareable");

    if (isShareable === "true" && role === "student") {
      const pathSegments = location.pathname.split("/");
      const videoId = pathSegments[pathSegments.length - 1];
      window.location.replace(
        `https://peerscroller.onelink.me/gTJq/fydlv6xg?videoId=${videoId}&af_force_deeplink=true`
      );
      return null;
    }
  }

  if (!rehydrated) return <LoaderComponent />;

  if (!authToken || !hasAuthenticatedUserData(userData)) {
    if (!hasAuthenticatedUserData(userData) && localStorage.getItem("token")) {
      localStorage.removeItem("token");
    }
    return (
      <Navigate
        to="/"
        state={location.pathname === "/settings" ? {} : { from: location }}
        replace
      />
    );
  }

  return isAllowedAuthenticatedRoute(location.pathname, userData) ? (
    <Outlet />
  ) : (
    <Navigate to={getAuthenticatedHomeRoute(userData)} replace />
  );
};

export default PrivateAuthRoutes;
